import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { buildPurchaseOrderColumns } from '../components/purchase-order-columns'
import { OrderingNotConfigured } from '../components/ordering-not-configured'
import { usePurchaseOrderRestaurantStore } from '../store/purchase-order-restaurant-store'
import {
  PURCHASE_ORDERS_KEY,
  useCreatePurchaseOrder,
  useOrderingStatus,
} from '../hooks/use-purchase-orders'
import { nextWeekdayIso } from '../lib/purchase-order-lines'
import { purchaseOrderErrorMessage } from '../lib/purchase-order-errors'
import type {
  PurchaseOrder,
  PurchaseOrderStatus,
} from '@/api/endpoints/purchase-orders'
import { purchaseOrdersApi } from '@/api/endpoints/purchase-orders'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { DataTable } from '@/components/data-table/data-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { useIsMobile } from '@/hooks/use-mobile'

const STATUS_FILTERS = ['all', 'open', 'sent', 'delivered'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/** "Offen" is everything that still needs the manager: drafts, failed and unclear sends. */
const STATUS_PARAM: Record<
  StatusFilter,
  Array<PurchaseOrderStatus> | undefined
> = {
  all: undefined,
  open: ['DRAFT', 'FAILED', 'SENDING'],
  sent: ['SENT'],
  delivered: ['DELIVERED'],
}

/** Lager → Bestellungen (CHG-2026-10-edi-purchase-orders, design UI). */
export function PurchaseOrderListPage() {
  return (
    <PermissionGate action={ACTIONS.PURCHASE_ORDER_VIEW}>
      <PurchaseOrderListContent />
    </PermissionGate>
  )
}

/** Behind the gate, so nothing is fetched without the permission. */
function PurchaseOrderListContent() {
  const { t } = useTranslation('purchase-orders')
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { can } = usePermissions()
  const canOrder = can(ACTIONS.PURCHASE_ORDER_ORDER)
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const restaurantId = usePurchaseOrderRestaurantStore((s) => s.restaurantId)
  const setRestaurantId = usePurchaseOrderRestaurantStore(
    (s) => s.setRestaurantId,
  )
  const [status, setStatus] = useState<StatusFilter>('all')
  const ordering = useOrderingStatus(restaurantId)
  const create = useCreatePurchaseOrder(restaurantId)

  useEffect(() => {
    if (
      restaurants.length > 0 &&
      (restaurantId == null || !restaurants.some((r) => r.id === restaurantId))
    ) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurants, restaurantId, setRestaurantId])

  const columns = useMemo(
    () => buildPurchaseOrderColumns(t, { compact: isMobile }),
    [t, isMobile],
  )

  async function fetchOrders({
    pageIndex,
    pageSize,
  }: {
    pageIndex: number
    pageSize: number
  }) {
    if (!restaurantId) return { rows: [], total: 0, pageCount: 0 }
    const res = await purchaseOrdersApi.list(restaurantId, {
      status: STATUS_PARAM[status],
      offset: pageIndex * pageSize,
      limit: pageSize,
    })
    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / pageSize),
    }
  }

  const newOrder = () =>
    create.mutate(
      { deliveryDate: nextWeekdayIso(new Date()), lines: [] },
      {
        onSuccess: (order) =>
          navigate({
            to: '/inventory/purchase-orders/$id',
            params: { id: order.id },
          }),
        onError: (error) => toast.error(purchaseOrderErrorMessage(error, t)),
      },
    )

  const newButton = canOrder ? (
    <Button onClick={newOrder} disabled={!restaurantId || create.isPending}>
      {t('list.new')}
    </Button>
  ) : null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{t('list.title')}</h1>
        {newButton}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={restaurantId != null ? String(restaurantId) : undefined}
          onValueChange={(v) => setRestaurantId(Number(v))}
        >
          <SelectTrigger className="w-56" aria-label={t('list.location')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {restaurants.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ToggleGroup
          type="single"
          variant="outline"
          value={status}
          onValueChange={(v) => v && setStatus(v as StatusFilter)}
          aria-label={t('list.filter.label')}
        >
          {STATUS_FILTERS.map((value) => (
            <ToggleGroupItem key={value} value={value}>
              {t(`list.filter.${value}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {ordering.data && !ordering.data.configured && restaurantId != null && (
        <OrderingNotConfigured
          restaurantId={restaurantId}
          missing={ordering.data.missing}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable<PurchaseOrder>
            queryKey={`${PURCHASE_ORDERS_KEY}-${restaurantId ?? 'none'}-${status}`}
            columns={columns}
            fetcher={fetchOrders}
            globalSearch={false}
            selectable={false}
            showDensityToggle={false}
            enableColumnVisibility={false}
            initialState={{ pageSize: 25 }}
            getRowId={(row) => row.id}
            emptyState={
              <div className="flex flex-col items-center gap-3 py-8">
                <p className="text-sm text-muted-foreground">
                  {t('list.empty')}
                </p>
                {newButton}
              </div>
            }
            onRowClick={(row) =>
              navigate({
                to: '/inventory/purchase-orders/$id',
                params: { id: row.original.id },
              })
            }
          />
        </CardContent>
      </Card>
    </div>
  )
}
